import { useEffect } from 'react';
import { connect } from 'react-redux';
import { FormattedDate, FormattedMessage } from 'react-intl';
import { Button } from '@iwoca/orion';

import {
    getApplicationRequestedAmount,
    getPrimaryAccountLoanStatus,
    hasOpenApplication
} from '../../../../multiLoanDashboard/multiLoanDashboardSelector';
import {
    getLatestOnlineAccountExpiryDate,
    getLatestOnlineAccountProvider,
    isLatestOnlineAccountNotActive
} from '../../../../../api/lendingAPI/OnlineAccountsSelectors';
import { OPEN_MODAL } from '../../../../../iwModals/Modals.actions';
import {
    canRequestTopUp,
    getMaxCashAmount,
    getTimeOutDate,
    isTopUpApproved,
    REVENUE_BASED_LOAN_PRODUCT_TYPE
} from '../../../../multiLoanDashboard/accountsSelector';
import { getCountry } from '../../../../../iwIntlProvider/CountryProvider';

import FutureRepayments from './FutureRepayments';
import { LOAN_STATUSES } from '../../../../dashboard/constants';
import { WarningTriangleSVG } from '../../../../../svgComponents/warningTriangleSVG/WarningTriangleSVG';
import DashboardCTA from '../../../../multiLoanDashboard/dashboardComponents/DashboardCTA/DashboardCTA';
import OpenBankingLink, {
    calculateRelinkDateFromExpiry
} from './OpenBankingLink';
import PastRepayments from '../../../../multiLoanDashboard/dashboardComponents/pastRepayments/PastRepayments';

import dashboardCtaStyles from '../../../../multiLoanDashboard/dashboardComponents/DashboardCTA/DashboardCTA.module.css';
import messages from './DashboardActions.messages';
import { TopupEligibleSVG } from '../../../../../svgComponents/TopupEligibleSVG/TopupEligibleSVG';
import { REQUEST_CHANGE_ROUTER } from '../../../../../iwRouter/IwRouter.actions';
import { TopupPendingSVG } from '../../../../../svgComponents/TopupPendingSVG/TopupPendingSVG';
import { MoneyWithDecimals } from '../../../../ui/moneyWithDecimals';
import { RocketSVG } from '../../../../../svgComponents/rocketSVG/RocketSVG';
import { TopupInEligibleSVG } from '@svgComponents/TopupInEligibleSVG/TopupInEligibleSVG';
import {
    canTopUpAfterRepayments,
    amountToRepayBeforeTopUp
} from '../../../selectors';
import {
    TOP_UP_ACTION,
    TOP_UP_DRAWDOWN_ACTION
} from '../../../../sharedDashboardComponents/LoanProgressBar/DashboardActions.types';

const mapStateToProps = (state) => {
    return {
        provider: getLatestOnlineAccountProvider(state),
        onlineAccountsLoaded: state.revenueBasedLoanDashboard.outstandingReady,
        isOpenBankingNotActive: isLatestOnlineAccountNotActive(state),
        loanStatus: getPrimaryAccountLoanStatus(state),
        openBankingExpiryDate: getLatestOnlineAccountExpiryDate(state),
        topUpApprovalTimeOut: getTimeOutDate(
            state,
            REVENUE_BASED_LOAN_PRODUCT_TYPE
        ),
        availableCredit: getMaxCashAmount(
            state,
            REVENUE_BASED_LOAN_PRODUCT_TYPE
        ),
        topUpAmount: getApplicationRequestedAmount(
            state,
            REVENUE_BASED_LOAN_PRODUCT_TYPE
        ),
        isTopUpEligible: canRequestTopUp(
            state,
            REVENUE_BASED_LOAN_PRODUCT_TYPE
        ),
        canTopUpAfterRepayments: canTopUpAfterRepayments(state),
        amountToRepayBeforeTopUp: amountToRepayBeforeTopUp(state),
        isTopUpPending: hasOpenApplication(
            state,
            REVENUE_BASED_LOAN_PRODUCT_TYPE
        ),
        isTopUpApproved: isTopUpApproved(state, REVENUE_BASED_LOAN_PRODUCT_TYPE)
    };
};

const mapDispatchToProps = (dispatch) => ({
    openLinkBankingModal: () =>
        dispatch({
            type: OPEN_MODAL,
            id: 'relinkExpiredBankingModal'
        }),
    openHelpModal: () =>
        dispatch({
            type: OPEN_MODAL,
            id: 'HelpModal'
        }),
    requestReapply: () =>
        dispatch({
            type: REQUEST_CHANGE_ROUTER,
            location: {
                pathname: '/apply-flexi-loan/'
            }
        })
});

const DashboardActions = ({
    accountId,
    provider,
    openLinkBankingModal,
    onlineAccountsLoaded,
    openBankingExpiryDate,
    isOpenBankingNotActive,
    loanStatus,
    openHelpModal,
    requestReapply,
    setActiveDashboardAction,
    topUpApprovalTimeOut,
    availableCredit,
    topUpAmount,
    isTopUpEligible,
    isTopUpPending,
    isTopUpApproved,
    canTopUpAfterRepayments,
    amountToRepayBeforeTopUp
}) => {
    useEffect(() => {
        if (
            getCountry() === 'UK' &&
            loanStatus !== LOAN_STATUSES.ENDED &&
            onlineAccountsLoaded &&
            provider &&
            isOpenBankingNotActive
        ) {
            openLinkBankingModal();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [onlineAccountsLoaded, isOpenBankingNotActive]);

    const isUK = getCountry() === 'UK';
    const daysUntilRelink = calculateRelinkDateFromExpiry(
        openBankingExpiryDate
    );
    const isOpenBankingExpiringSoon = isUK && daysUntilRelink <= 14;
    const showReEngagementFlow = isUK && loanStatus === LOAN_STATUSES.ENDED;

    const isDE = getCountry() === 'DE';
    const isTopUpEligibleDE = isDE && isTopUpEligible;
    const isTopUpApprovedDE = isDE && isTopUpApproved;
    const isTopUpPendingDE = isDE && isTopUpPending;
    const canTopUpAfterRepaymentsDE = isDE && canTopUpAfterRepayments;

    return (
        <>
            {loanStatus !== LOAN_STATUSES.ENDED && (
                <>
                    {loanStatus === LOAN_STATUSES.OVERDUE && (
                        <DashboardCTA
                            icon={<WarningTriangleSVG />}
                            className={
                                dashboardCtaStyles.nonBottomDashboardAction
                            }
                            data-testid="rbl-overdue-repayment-action"
                            title={
                                <FormattedMessage
                                    {...messages.overdueRepaymentTitle}
                                />
                            }
                            description={
                                <FormattedMessage
                                    {...messages.overdueRepaymentDescription}
                                />
                            }
                            cta={
                                <Button
                                    variant="action"
                                    className={dashboardCtaStyles.link}
                                    onClick={openHelpModal}
                                >
                                    <FormattedMessage
                                        {...messages.overdueRepaymentCTA}
                                    />
                                </Button>
                            }
                        />
                    )}

                    {isOpenBankingExpiringSoon && (
                        <OpenBankingLink
                            isOpenBankingExpiringSoon={true}
                            className={
                                dashboardCtaStyles.nonBottomDashboardAction
                            }
                        />
                    )}

                    {getCountry() === 'UK' && (
                        <FutureRepayments
                            className={
                                dashboardCtaStyles.nonBottomDashboardAction
                            }
                        />
                    )}
                </>
            )}

            {showReEngagementFlow && (
                <DashboardCTA
                    data-testid="top-up-eligible-action"
                    title={<FormattedMessage {...messages.reapplyTitle} />}
                    className={dashboardCtaStyles.nonBottomDashboardAction}
                    icon={<TopupEligibleSVG />}
                    description={
                        <FormattedMessage {...messages.reapplyDescription} />
                    }
                    cta={
                        <Button
                            data-testid="reapply-action-button"
                            variant="primary"
                            onClick={() => requestReapply()}
                            className={dashboardCtaStyles.dashboardActionCTA}
                        >
                            <FormattedMessage
                                {...messages.createReapplyRequest}
                            />
                        </Button>
                    }
                />
            )}

            {isTopUpEligibleDE && (
                <DashboardCTA
                    data-testid="top-up-eligible-action"
                    title={<FormattedMessage {...messages.reapplyTitle} />}
                    className={dashboardCtaStyles.nonBottomDashboardAction}
                    icon={<TopupEligibleSVG />}
                    description={
                        <FormattedMessage {...messages.reapplyDescription} />
                    }
                    cta={
                        <Button
                            data-testid="reapply-action-button"
                            variant="primary"
                            onClick={() =>
                                setActiveDashboardAction(TOP_UP_ACTION)
                            }
                            className={dashboardCtaStyles.dashboardActionCTA}
                        >
                            <FormattedMessage
                                {...messages.createReapplyRequest}
                            />
                        </Button>
                    }
                />
            )}

            {canTopUpAfterRepaymentsDE && (
                <DashboardCTA
                    data-testid="drawdown-canTopUpAfterRepayments"
                    title={
                        <FormattedMessage {...messages.createReapplyRequest} />
                    }
                    className={dashboardCtaStyles.nonBottomDashboardAction}
                    icon={<TopupInEligibleSVG />}
                    description={
                        <FormattedMessage
                            {...messages.amountToRepayBeforeTopupEligible}
                            values={{
                                amountToRepayBeforeTopUp: (
                                    <strong>
                                        <MoneyWithDecimals
                                            value={amountToRepayBeforeTopUp}
                                        />
                                    </strong>
                                )
                            }}
                        />
                    }
                    cta={
                        <Button
                            disabled
                            variant="success"
                            data-testid="drawdown-canTopUpAfterRepayments-action"
                            className={dashboardCtaStyles.dashboardActionCTA}
                        >
                            <FormattedMessage
                                {...messages.createReapplyRequest}
                            />
                        </Button>
                    }
                />
            )}

            {isTopUpApprovedDE && (
                <DashboardCTA
                    data-testid="drawdown-topup-action"
                    title={
                        <FormattedMessage {...messages.topupApprovedTitle} />
                    }
                    className={dashboardCtaStyles.nonBottomDashboardAction}
                    icon={<RocketSVG />}
                    description={
                        <FormattedMessage
                            {...messages.topupApprovedDescription}
                            values={{
                                amount: (
                                    <span
                                        className={
                                            dashboardCtaStyles.topupApprovedAmount
                                        }
                                    >
                                        <MoneyWithDecimals
                                            value={availableCredit}
                                        />
                                    </span>
                                ),
                                expiryDate: (
                                    <span
                                        className={
                                            dashboardCtaStyles.topupApprovedExpiryDate
                                        }
                                    >
                                        <FormattedDate
                                            value={
                                                new Date(topUpApprovalTimeOut)
                                            }
                                            day="2-digit"
                                            month="short"
                                            year="numeric"
                                        />
                                    </span>
                                )
                            }}
                        />
                    }
                    cta={
                        <Button
                            variant="success"
                            data-testid="drawdown-topup-button"
                            onClick={() => {
                                setActiveDashboardAction(
                                    TOP_UP_DRAWDOWN_ACTION
                                );
                            }}
                            className={dashboardCtaStyles.dashboardActionCTA}
                        >
                            <FormattedMessage
                                {...messages.chooseDrawdownAmount}
                            />
                        </Button>
                    }
                />
            )}

            {isTopUpPendingDE && (
                <DashboardCTA
                    data-testid="top-up-pending-action"
                    title={<FormattedMessage {...messages.topupPendingTitle} />}
                    className={dashboardCtaStyles.nonBottomDashboardAction}
                    icon={<TopupPendingSVG />}
                    description={
                        <FormattedMessage
                            {...messages.topupPendingDescription}
                            values={{
                                amount: (
                                    <span
                                        className={
                                            dashboardCtaStyles.topupPendingAmount
                                        }
                                    >
                                        <MoneyWithDecimals
                                            value={topUpAmount}
                                        />
                                    </span>
                                )
                            }}
                        />
                    }
                    cta={
                        <Button
                            variant="primary"
                            onClick={openHelpModal}
                            className={dashboardCtaStyles.dashboardActionCTA}
                        >
                            <FormattedMessage
                                {...messages.changeTopupRequest}
                            />
                        </Button>
                    }
                />
            )}

            {accountId && (
                <PastRepayments
                    accountId={accountId}
                    productType={REVENUE_BASED_LOAN_PRODUCT_TYPE}
                    className={
                        getCountry() === 'UK' &&
                        loanStatus !== LOAN_STATUSES.ENDED &&
                        !isOpenBankingExpiringSoon &&
                        dashboardCtaStyles.nonBottomDashboardAction
                    }
                />
            )}

            {isUK &&
                loanStatus !== LOAN_STATUSES.ENDED &&
                !isOpenBankingExpiringSoon && (
                    <OpenBankingLink isOpenBankingExpiringSoon={false} />
                )}
        </>
    );
};

// eslint-disable-next-line import/no-default-export
export default connect(mapStateToProps, mapDispatchToProps)(DashboardActions);
